import { useEffect, useState } from "react";
import { api } from "../api/client";
import type { FollowReportDTO } from "../api/types";
import { sanitizeRichText } from "../utils/format";
import { useToast } from "./Toast";

interface PreviewModalArchive {
  id: string;
  name: string;
  type: string;
  deptName: string;
  bizDate: string;
  /** 微盘已同步时加载微盘纪要正文 */
  driveReady?: boolean;
  /** 是否有本地正文（引擎生成或人工编辑） */
  hasContent?: boolean;
}

interface PreviewModalProps {
  archive: PreviewModalArchive | null;
  onClose: () => void;
}

/** 纪要预览弹窗：微盘已同步 → 加载真实纪要正文；无正文 → 空态提示 */
export function PreviewModal({ archive, onClose }: PreviewModalProps) {
  const [content, setContent] = useState<string | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [contentError, setContentError] = useState<string | null>(null);

  useEffect(() => {
    if (!archive) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [archive, onClose]);

  // 有正文（本地或微盘）的纪要：拉取正文
  useEffect(() => {
    if (!archive?.hasContent && !archive?.driveReady) {
      setContent(null);
      return;
    }
    let cancelled = false;
    setLoadingContent(true);
    setContentError(null);
    api.archives
      .content(archive.id)
      .then((res) => {
        if (!cancelled) setContent(res.content);
      })
      .catch((err: unknown) => {
        if (!cancelled) setContentError(err instanceof Error ? err.message : "纪要内容加载失败");
      })
      .finally(() => {
        if (!cancelled) setLoadingContent(false);
      });
    return () => {
      cancelled = true;
    };
  }, [archive?.id, archive?.driveReady, archive?.hasContent]);

  if (!archive) return null;

  return (
    <div
      className="modal-mask show"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal" role="dialog" aria-modal="true" aria-label={archive.name}>
        <div className="modal-head">
          <span>{archive.name}</span>
          <button className="modal-close" onClick={onClose} aria-label="关闭预览">
            ✕
          </button>
        </div>
        <div className="modal-body">
          {archive.driveReady || archive.hasContent ? (
            loadingContent ? (
              <div className="loading-box">
                <span className="spinner" />
                正在加载纪要正文…
              </div>
            ) : contentError ? (
              <p className="content-error">{contentError}</p>
            ) : (
              <pre className="minutes-content">{content}</pre>
            )
          ) : (
            <p className="text-muted">
              该纪要暂无正文内容。参会人提交纪要后将自动归档，可稍后刷新查看。
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** 议程跟进报告（大模型生成，三卡片 + 小结） */
interface FollowReportProps {
  report: FollowReportDTO | null;
  deptOptions: { id: string; name: string }[];
  periodOptions: { label: string; value: string }[];
  selectedDeptId: string | null;
  period: string;
  loading: boolean;
  generating: boolean;
  onDeptChange: (deptId: string) => void;
  onPeriodChange: (period: string) => void;
  onGenerate: () => void;
}

const TODO_STATE: Record<string, { cls: string; icon: string; text: string }> = {
  DONE: { cls: "done", icon: "✓", text: "已完成" },
  DOING: { cls: "doing", icon: "…", text: "进行中" },
  PENDING: { cls: "pending", icon: "!", text: "未开始" },
};

export function FollowReport({
  report,
  deptOptions,
  periodOptions,
  selectedDeptId,
  period,
  loading,
  generating,
  onDeptChange,
  onPeriodChange,
  onGenerate,
}: FollowReportProps) {
  const { show } = useToast();

  return (
    <section className="panel follow-panel" id="secFollow">
      <div className="panel-head">
        <div className="panel-title">大模型议程跟进报告</div>
        <span className="ai-badge">🤖 基于会议纪要自动生成</span>
      </div>
      <div className="panel-body">
        <div className="follow-select-row">
          <label htmlFor="followDept">
            选择部门：
          </label>
          <select id="followDept" value={selectedDeptId ?? ""} onChange={(e) => onDeptChange(e.target.value)}>
            {deptOptions.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>
          <label htmlFor="followPeriod">
            跟进周期：
          </label>
          <select id="followPeriod" value={period} onChange={(e) => onPeriodChange(e.target.value)}>
            {periodOptions.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <button className="btn btn-primary" disabled={generating || !selectedDeptId} onClick={onGenerate}>
            {generating ? "⏳ 大模型生成中…" : "⚡ 重新生成跟进报告"}
          </button>
        </div>

        {loading && !report ? (
          <div className="loading-box">
            <span className="spinner" />
            正在生成跟进报告…
          </div>
        ) : !report ? (
          <div className="empty-note">暂无报告数据</div>
        ) : (
          <>
            <div className="report-meta">
              <span>
                周期：<b>{report.period}</b>
              </span>
              <span>来源：{report.generatedBy}</span>
              <span>{report.cached ? "缓存报告" : "实时生成"}</span>
              <span className="link" onClick={() => show("点击「重新生成」可强制刷新报告")}>
                如何刷新？
              </span>
            </div>
            <div className="follow-grid">
              {/* 业绩情况 */}
              <div className="follow-card">
                <div className="follow-card-head perf">📊 部门业绩情况</div>
                <div className="follow-card-body">
                  {report.perf.map((p, i) => (
                    <div className="perf-row" key={`${p.name}-${i}`}>
                      <span className="k">{p.name}</span>
                      <span>
                        <span className="v">{p.value}</span>&nbsp;&nbsp;
                        <span className={p.trend}>
                          {p.trend === "up" ? "▲" : "▼"} {p.delta}
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 待办跟进 */}
              <div className="follow-card">
                <div className="follow-card-head todo">📌 会议待办跟进情况</div>
                <div className="follow-card-body">
                  <div className="todo-summary-bar">
                    <span>共 {report.todos.length} 项待办</span>
                    <span>
                      完成率{" "}
                      {report.todos.length
                        ? Math.round((report.todos.filter((t) => t.status === "DONE").length / report.todos.length) * 100)
                        : 0}
                      %
                    </span>
                  </div>
                  {report.todos.map((t) => {
                    const state = TODO_STATE[t.status] ?? TODO_STATE.PENDING;
                    return (
                      <div className="todo-item" key={t.id}>
                        <div className={`todo-check ${state.cls}`}>{state.icon}</div>
                        <div className="t-body">
                          <div>{t.text}</div>
                          <div className="t-owner">
                            {t.ownerName ?? "未指派"}
                            {t.dueDate ? ` · 截止 ${t.dueDate}` : ""} · {state.text}
                          </div>
                          <div className="progress-line">
                            <i style={{ width: `${t.progress}%` }} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 下次会议建议议程 */}
              <div className="follow-card">
                <div className="follow-card-head plan">🧭 下次会议建议议程</div>
                <div className="follow-card-body">
                  {report.plans.map((p, i) => (
                    <div className="plan-item" key={i}>
                      <div className="idx">{i + 1}</div>
                      <div>{p}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="follow-summary">
              <b>🤖 AI 跟进小结：</b>
              <span dangerouslySetInnerHTML={{ __html: sanitizeRichText(report.summary) }} />
            </div>
          </>
        )}
      </div>
    </section>
  );
}
